import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCOPE_CHANGE_IMPACTS } from '@lims/shared-types';
import { workOrdersApi } from '../../api/endpoints.js';
import type {
  CatalogueItem,
  Department,
  WorkOrderDetail,
} from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxField,
  Dialog,
  ErrorState,
  Form,
  TextAreaField,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import {
  ServiceLineEditor,
  ServiceRefusal,
  draftFromService,
  emptyServiceLine,
  servicePayload,
} from './ServiceLineEditor.js';
import type { ServiceLineDraft } from './ServiceLineEditor.js';

/**
 * Renegotiate what a request asks for.
 *
 * ===========================================================================
 * THREE SEPARATE ACKNOWLEDGEMENTS, NOT ONE "I UNDERSTAND" BOX.
 * ===========================================================================
 *
 * Once any work has started, the API refuses a scope change unless the price,
 * the deadline and the work already in progress are EACH acknowledged. One
 * combined box would be ticked without reading; three named consequences are
 * what the person is actually agreeing to.
 *
 * THIS DIALOG DOES NOT ENFORCE THAT RULE — THE SERVER DOES. Hiding a control
 * is never a security mechanism (CLAUDE.md section 5.1). What this dialog does
 * is ask for the acknowledgements before the round trip, so the refusal is not
 * something the user discovers after retyping everything.
 *
 * WORK ALREADY DONE IS NOT ERASED. Every saved service is sent back WITH ITS
 * ID (D195): an untouched one is kept exactly as it is, a changed one is
 * changed in place, and a started or completed one is shown locked. Before,
 * this dialog sent the list without ids, so the server re-created every line —
 * re-pricing it from today's catalogue — and cancelled every service already
 * in progress, although this dialog called them retained. Cancelled services
 * are history and are listed separately.
 */
export function WorkOrderScopeDialog({
  workOrder,
  isOpen,
  onClose,
  onSaved,
  catalogue,
  departments,
  canOverride,
}: {
  readonly workOrder: WorkOrderDetail;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => Promise<void> | void;
  readonly catalogue: readonly CatalogueItem[];
  readonly departments: readonly Department[];
  readonly canOverride: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [lines, setLines] = useState<ServiceLineDraft[]>([]);
  const [acknowledged, setAcknowledged] = useState({
    price: false,
    deadline: false,
    workInProgress: false,
  });
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  /** Cancelled services: history, never scope. */
  const retained = workOrder.services.filter(
    (service) => service.state === 'cancelled',
  );

  useEffect(() => {
    if (!isOpen) return;
    // Every saved service that is not cancelled, WITH its id: kept unless
    // changed or removed here; a started one is locked by the editor.
    const current = workOrder.services
      .filter((service) => service.state !== 'cancelled')
      .map<ServiceLineDraft>(draftFromService);
    setLines(current.length > 0 ? current : [emptyServiceLine()]);
    setAcknowledged({ price: false, deadline: false, workInProgress: false });
    setNote('');
    setReason('');
  }, [isOpen, workOrder.services]);

  const save = useMutation({
    mutationFn: () =>
      workOrdersApi.setServices(workOrder.id, {
        services: servicePayload(lines),
        // Sent ONLY when all three are actually ticked. A partly-filled
        // acknowledgement is not an acknowledgement, and sending `false`
        // would satisfy the field while affirming nothing.
        ...(acknowledged.price &&
        acknowledged.deadline &&
        acknowledged.workInProgress
          ? {
              impactAcknowledgement: {
                price: true as const,
                deadline: true as const,
                workInProgress: true as const,
                ...(note.trim() ? { note: note.trim() } : {}),
              },
            }
          : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
  });

  const failureCode = (code: string) =>
    save.error instanceof ApiError &&
    save.error.details?.some((detail) => detail.code === code);

  const needsAcknowledgement = workOrder.scopeChangeNeedsAcknowledgement;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('workOrders.scopeChangeTitle')}
      size="xl"
    >
      {needsAcknowledgement ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('workOrders.scopeStartedNotice')}
            description={t('workOrders.scopeStartedHint')}
          />
        </div>
      ) : null}

      {failureCode('impact_not_acknowledged') ? (
        <div className="mb-stack">
          <Alert tone="warning" title={t('workOrders.ackRequiredNotice')} />
        </div>
      ) : null}
      {failureCode('work_order_complete') ? (
        <div className="mb-stack">
          <Alert tone="warning" title={t('workOrders.workOrderComplete')} />
        </div>
      ) : null}
      {failureCode('work_order_cancelled') ? (
        <div className="mb-stack">
          <Alert tone="warning" title={t('workOrders.workOrderCancelled')} />
        </div>
      ) : null}
      {failureCode('impact_not_acknowledged') ? null : <ServiceRefusal error={save.error} />}

      <Form
        onSubmit={() => save.mutate()}
        isSubmitting={save.isPending}
        actions={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={onClose}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              type="submit"
              isLoading={save.isPending}
            >
              {t('workOrders.scopeChangeSave')}
            </ActionButton>
          </ActionBar>
        }
      >
        {retained.length > 0 ? (
          <div className="min-w-0 rounded-medium border border-default-200 p-3">
            <p className="text-body font-medium text-default-800">
              {t('workOrders.retainedTitle')}
            </p>
            <p className="mb-stack text-caption text-default-600">
              {t('workOrders.retainedHint')}
            </p>
            <ul className="flex flex-col gap-1">
              {retained.map((service) => (
                <li key={service.id} className="text-caption text-default-700">
                  {((locale === 'ar'
                    ? service.description.ar
                    : service.description.en) ?? service.itemCode) ||
                    t('common.notRecorded')}{' '}
                  — {t(`workOrderServiceState.${service.state}`)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <ServiceLineEditor
          lines={lines}
          onChange={setLines}
          catalogue={catalogue}
          departments={departments}
          canOverride={canOverride}
          currency={workOrder.currency}
        />

        <ActionButton
          priority="secondary"
          size="sm"
          onPress={() => setLines((current) => [...current, emptyServiceLine()])}
        >
          {t('workOrders.addService')}
        </ActionButton>

        {needsAcknowledgement ? (
          <fieldset className="min-w-0 border-0 p-0">
            <legend className="mb-stack text-section font-medium text-default-800">
              {t('workOrders.ackTitle')}
            </legend>
            <div className="flex flex-col gap-stack">
              {SCOPE_CHANGE_IMPACTS.map((impact) => (
                <CheckboxField
                  key={impact}
                  label={t(`workOrders.ack.${impact}`)}
                  description={t(`workOrders.ackHint.${impact}`)}
                  value={acknowledged[impact]}
                  onChange={(value) =>
                    setAcknowledged((current) => ({
                      ...current,
                      [impact]: value,
                    }))
                  }
                />
              ))}
            </div>
            <div className="mt-stack">
              <TextAreaField
                label={t('workOrders.ackNote')}
                value={note}
                onChange={setNote}
                rows={2}
              />
            </div>
          </fieldset>
        ) : null}

        <TextAreaField
          label={t('common.reasonOptional')}
          value={reason}
          onChange={setReason}
          rows={2}
        />

        {save.error ? <ErrorState error={save.error} /> : null}
      </Form>
    </Dialog>
  );
}
